import React, { useState } from 'react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  Legend 
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { cropYieldData, provinces, crops } from '../data/mockData';

const YieldChart = () => {
  const [selectedCrop, setSelectedCrop] = useState('Wheat');
  const [selectedMetric, setSelectedMetric] = useState('yield');
  
  const prepareChartData = () => {
    const years = Array.from({length: 10}, (_, i) => 2015 + i);
    
    return years.map(year => {
      const dataPoint = { year };
      provinces.forEach(province => {
        const provinceData = cropYieldData[selectedCrop][province.id];
        const yearData = provinceData.find(d => d.year === year);
        if (yearData) {
          dataPoint[province.id] = yearData[selectedMetric];
        }
      });
      return dataPoint;
    });
  };

  const getMetricLabel = () => {
    switch(selectedMetric) {
      case 'yield': return 'Yield (tonnes/ha)';
      case 'area': return 'Area (hectares)';
      case 'production': return 'Production (tonnes)';
      default: return 'Yield (tonnes/ha)';
    }
  };

  const formatTooltipValue = (value, name) => {
    const province = provinces.find(p => p.id === name);
    const provinceName = province ? province.name : name;
    
    let formattedValue = value;
    if (selectedMetric === 'area' || selectedMetric === 'production') {
      formattedValue = value.toLocaleString();
    }
    
    return [formattedValue, provinceName];
  };

  const colors = {
    'ON': '#8884d8',
    'SK': '#82ca9d',
    'AB': '#ffc658',
    'MB': '#ff7300'
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Crop {getMetricLabel()} Trends</span>
          <div className="flex gap-2">
            <Select value={selectedCrop} onValueChange={setSelectedCrop}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {crops.map(crop => (
                  <SelectItem key={crop} value={crop}>
                    {crop}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedMetric} onValueChange={setSelectedMetric}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="yield">Yield</SelectItem>
                <SelectItem value="area">Area</SelectItem>
                <SelectItem value="production">Production</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={prepareChartData()}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" />
              <YAxis />
              <Tooltip 
                formatter={formatTooltipValue}
                labelFormatter={(value) => `Year: ${value}`}
              />
              <Legend />
              {provinces.map(province => (
                <Line
                  key={province.id}
                  type="monotone"
                  dataKey={province.id}
                  stroke={colors[province.id]}
                  strokeWidth={2}
                  dot={{ fill: colors[province.id], strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default YieldChart;